import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSession } from '@/features/auth/useSession';
import { usePlatformSession } from '@/features/platform/usePlatformSession';
import { useVendorSession } from '@/features/vendor/useVendorSession';
import { useGuidanceInternals } from '@/guidance/context';
import { excludedRouteFor } from '@/guidance/safeRoutes';
import { Avatar } from '@/shared/components/Avatar';
import { Menu, type MenuEntry, type MenuItem } from '@/shared/components/Menu';
import { useLocale } from '@/shared/i18n/useLocale';
import type { RoleSummary } from '@/shared/types/domain';
import { cn } from '@/shared/utils/cn';

import { AccessSummary, FirstAndCount } from './AccessSummary';
import { ACCOUNT_ROUTES, type AccountPortal } from './paths';
import { inRoleOrder } from './roleOrder';

/**
 * The account menu (PRF-01 / IA-01 / UI-5), one per shell header.
 *
 * The trigger is the person's Avatar — plus their name from 1024px — named
 * "Account: {name}". The menu says who is signed in and where, and offers:
 * My profile; Sign-in security (the tenant: always, for this satisfied
 * session; the operator: always; the supplier: not until a supplier password
 * contract exists); Help & tours; the display language; then, after a
 * divider, Sign out (D-PUX-09: Sign out lives here, at every width).
 *
 * OBS-007 (role context before a decision; PROVISIONAL, option A of the R3
 * decision table, pending the Product Owner and Design — proposed delta in
 * docs/design-sync/OBS_007_ROLE_CONTEXT_DELTA_PROPOSED.md) — in the tenant
 * shell, from 1024px a role line under the name: the person's first role in
 * the one order (`roleOrder.ts`) and "+N" for the others, as onboarding
 * counts a step's owners; every role is also the trigger's description. The
 * menu, and the phone sheet, add two lines to their header, the roles and
 * where the person works, each the first and "+N" (`AccessSummary`): lines
 * that never grow, so the rows under them, Sign out last, stay in view
 * without scrolling. A screen reader hears every name; My profile lists them
 * all. Below 1024px the bar is unchanged: the avatar alone.
 *
 * Nothing in it grants or changes authority: every row is a destination the
 * person already has, or their own sign-out or display language. There is no
 * organisation switch — a session belongs to one organisation, and the only
 * safe way to another is to sign out and choose at sign-in (PRF-06) — and no
 * role switch: a person holds all their roles at once, none of them primary,
 * and the roles are facts in the header, never rows.
 *
 * Each portal reads its OWN session, through its own guard's hooks; the three
 * never share one.
 *
 * Sign out is the SHELL's: the shell that reads the session holds the
 * sign-out mutation and passes it here. The supplier and operator sign-outs
 * drop their session from the cache rather than re-reading it, so the shell
 * gives way only because the mutation it holds re-renders it; held here
 * instead, the console stayed on screen after a sign-out (pux-account e2e).
 */
export interface AccountSignOut {
  run: () => void;
  pending: boolean;
}

export function AccountMenu({
  portal,
  signOut,
}: {
  portal: AccountPortal;
  signOut: AccountSignOut;
}) {
  if (portal === 'tenant') return <TenantAccountMenu signOut={signOut} />;
  if (portal === 'vendor') return <VendorAccountMenu signOut={signOut} />;

  return <PlatformAccountMenu signOut={signOut} />;
}

function TenantAccountMenu({ signOut }: { signOut: AccountSignOut }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const shared = useSharedRows();
  const name = session?.user.display_name ?? '';
  const roles = inRoleOrder(session?.user.roles ?? []);

  return (
    <AccountMenuView
      name={name}
      roles={roles}
      header={{
        title: name,
        meta: session?.tenant?.display_name,
        details: <AccessSummary roles={roles} scopes={session?.user.scopes ?? []} />,
      }}
      busy={signOut.pending}
      items={[
        { id: 'profile', label: t('account.menu.profile'), to: ACCOUNT_ROUTES.tenant.profile },
        {
          id: 'security',
          label: t('nav.signInSecurity'),
          to: ACCOUNT_ROUTES.tenant.security,
        },
        ...shared,
        { divider: true },
        signOutRow(t('auth.signOut'), signOut.run),
      ]}
    />
  );
}

function VendorAccountMenu({ signOut }: { signOut: AccountSignOut }) {
  const { t } = useTranslation();
  const { user } = useVendorSession();
  const shared = useSharedRows();
  const name = user?.user.name ?? '';
  const supplier = user?.supplier.display_name ?? null;
  const buyer = user?.buyer.display_name ?? null;

  return (
    <AccountMenuView
      name={name}
      header={{
        title: name,
        // Whom this person works for, and whom they supply — never a list.
        meta:
          supplier || buyer ? (
            <>
              {supplier ? <bdi>{supplier}</bdi> : null}
              {supplier && buyer ? ' · ' : null}
              {buyer ? (
                <>
                  {t('vendor.shell.supplying')} <bdi>{buyer}</bdi>
                </>
              ) : null}
            </>
          ) : undefined,
      }}
      busy={signOut.pending}
      items={[
        {
          id: 'profile',
          label: t('account.menu.vendorAccount'),
          to: ACCOUNT_ROUTES.vendor.profile,
        },
        ...shared,
        { divider: true },
        signOutRow(t('auth.signOut'), signOut.run),
      ]}
    />
  );
}

function PlatformAccountMenu({ signOut }: { signOut: AccountSignOut }) {
  const { t } = useTranslation();
  const { operator } = usePlatformSession();
  const shared = useSharedRows();
  const name = operator?.name ?? '';

  return (
    <AccountMenuView
      name={name}
      header={{ title: name, meta: operator?.role_label }}
      busy={signOut.pending}
      onDarkBar
      items={[
        { id: 'profile', label: t('account.menu.profile'), to: ACCOUNT_ROUTES.platform.profile },
        {
          id: 'security',
          label: t('nav.signInSecurity'),
          to: ACCOUNT_ROUTES.platform.security,
        },
        ...shared,
        { divider: true },
        signOutRow(t('auth.signOut'), signOut.run),
      ]}
    />
  );
}

/** R-050: Sign out goes last, after a divider, in the danger tone. */
function signOutRow(label: string, onSelect: () => void): MenuItem {
  return { id: 'sign-out', label, tone: 'danger', onSelect };
}

/**
 * Help & tours and the display language — the same in every portal.
 *
 * Help opens the shell's own Help panel, and is absent wherever the header's
 * Help entry is (no guidance root; a page that shows or collects a secret).
 * The language row is the header toggle again: this device's display only,
 * never the language the server writes to this person in.
 */
function useSharedRows(): MenuEntry[] {
  const { t } = useTranslation();
  const { locale, toggle } = useLocale();
  const guidance = useGuidanceInternals();
  const other = locale === 'ar' ? 'en' : 'ar';

  return [
    ...(guidance !== null && excludedRouteFor(guidance.ctx.pathname)?.currentPageGuide !== false
      ? [
          {
            id: 'help',
            label: t('guidance.help.entry'),
            onSelect: () => guidance.store.openPanel(),
          },
        ]
      : []),
    {
      id: 'language',
      label: t('account.menu.language'),
      trailing: <span lang={other}>{other === 'en' ? 'English' : 'العربية'}</span>,
      onSelect: toggle,
    },
  ];
}

function AccountMenuView({
  name,
  roles = [],
  header,
  items,
  busy,
  onDarkBar = false,
}: {
  name: string;
  /** OBS-007: the person's roles, already in the one order — the tenant's only. */
  roles?: RoleSummary[];
  header: { title: string; meta?: ReactNode; details?: ReactNode };
  items: MenuEntry[];
  busy: boolean;
  /** The operator's Deep Plum header: a light chip, the name in the shell's ink (A7: no raw white). */
  onDarkBar?: boolean;
}) {
  const { t } = useTranslation();
  const roleNames = roles.map((role) => role.display_name).join(t('amend.listSeparator'));

  return (
    <Menu
      label={t('account.menu.trigger', { name })}
      description={roles.length > 0 ? `${t('account.profile.roles')}: ${roleNames}` : undefined}
      align="end"
      header={header}
      items={items}
      busy={busy}
      triggerClassName={cn(onDarkBar && 'hover:bg-shell-hover')}
      trigger={
        <>
          <Avatar name={name} size="lg" onShell={!onDarkBar} />
          {/*
            The name joins the avatar from 1024px, with the role line under it
            (OBS-007); below that the bar has no room, and shows the avatar
            alone, as before.
          */}
          <span className="hidden max-w-40 min-w-0 flex-col pe-1.5 lg:flex">
            <span
              className={cn(
                'truncate type-label font-bold',
                onDarkBar ? 'text-ink-on-shell' : 'text-ink',
              )}
            >
              {name}
            </span>
            {roles.length > 0 ? (
              <span
                className={cn(
                  'flex min-w-0 gap-1 type-meta',
                  onDarkBar ? 'text-ink-on-shell-muted' : 'text-ink-muted',
                )}
              >
                {/* The first role and "+N" for the others (StepOwner's count). */}
                <FirstAndCount names={roles.map((role) => role.display_name)} />
              </span>
            ) : null}
          </span>
        </>
      }
    />
  );
}
